import type { AvaAIClientToolName, AvaAIToolName, AvaAIUITools } from '@talos/shared/avaAI';
import type { ChatAddToolOutputFunction, ChatOnToolCallCallback } from 'ai';

import type { AvaAIUIMessage } from '@src/providers/AvaAIContext';
import {
  CLIENT_TOOL_ERROR_MESSAGE,
  createClientToolExecutor,
  reportClientToolFailure,
  type ClientToolModule,
  type ReportClientToolFailure,
} from './clientToolExecutor';

export type AddClientToolOutput = ChatAddToolOutputFunction<AvaAIUIMessage>;
export type AvaAIClientToolCall = Parameters<ChatOnToolCallCallback<AvaAIUIMessage>>[0]['toolCall'];

interface ClientToolCall {
  toolCallId: string;
  input: unknown;
}

export interface AvaAIClientToolHandler<Name extends AvaAIClientToolName = AvaAIClientToolName> {
  name: Name;
  isAvailable: boolean;
  handleToolCall: (toolCall: ClientToolCall, addToolOutput: AddClientToolOutput) => Promise<void>;
  startUserTurn: () => void;
}

export type AvaAIClientToolRegistry = Partial<Record<AvaAIToolName, AvaAIClientToolHandler>> & {
  [Name in AvaAIClientToolName]: AvaAIClientToolHandler<Name>;
};

export function createAvaAIClientToolHandler<Name extends AvaAIClientToolName>(
  clientTool: ClientToolModule<Name, AvaAIUITools[Name]['output']>
): AvaAIClientToolHandler<Name> {
  const executor = createClientToolExecutor(clientTool);

  return {
    name: clientTool.name,
    isAvailable: clientTool.isAvailable !== false,
    async handleToolCall({ toolCallId, input }, addToolOutput) {
      const result = await executor.execute(input);
      if (result.status === 'failed') {
        await addToolOutput({
          tool: clientTool.name,
          toolCallId,
          state: 'output-error',
          errorText: result.message,
        });
        return;
      }

      await addToolOutput({
        tool: clientTool.name,
        toolCallId,
        state: 'output-available',
        output: result.output,
      });
    },
    startUserTurn: executor.startUserTurn,
  };
}

/**
 * Routes a call to the handler that owns it. A plain factory rather than hook-local logic, so the
 * routing and its failure paths are testable without rendering the composition root.
 */
export function createAvaAIClientToolRouter(
  clientTools: AvaAIClientToolRegistry,
  reportFailure: ReportClientToolFailure = reportClientToolFailure
) {
  const registeredClientTools = Object.values(clientTools).filter(
    (clientTool): clientTool is AvaAIClientToolHandler => clientTool != null
  );
  const supportedToolNames = Object.freeze(
    registeredClientTools.filter(clientTool => clientTool.isAvailable).map(clientTool => clientTool.name)
  );

  async function handleToolCall(toolCall: AvaAIClientToolCall, addToolOutput: AddClientToolOutput): Promise<void> {
    // A dynamic call is a tool the shared catalog no longer declares, such as one renamed or
    // retired behind a deployed browser; an unmatched static name is catalog drift the other way.
    const clientTool = toolCall.dynamic ? undefined : clientTools[toolCall.toolName];
    if (clientTool == null) {
      reportFailure({ category: 'MissingHandler', toolName: toolCall.toolName });
      // Every call must reach a terminal state or the turn stalls with no output. addToolOutput
      // resolves the part by toolCallId and ignores `tool`, so an undeclared name only needs the
      // cast to satisfy the SDK's typing of known tools.
      await addToolOutput({
        tool: toolCall.toolName as AvaAIToolName,
        toolCallId: toolCall.toolCallId,
        state: 'output-error',
        errorText: CLIENT_TOOL_ERROR_MESSAGE,
      });
      return;
    }

    await clientTool.handleToolCall({ toolCallId: toolCall.toolCallId, input: toolCall.input }, addToolOutput);
  }

  return {
    supportedToolNames,
    handleToolCall,
    startUserTurn: () => {
      registeredClientTools.forEach(clientTool => clientTool.startUserTurn());
    },
  };
}
